<script setup lang="ts">
import { isValidPhoneNumber, parsePhoneNumberWithError, type CountryCode } from 'libphonenumber-js';
import type { BtnSize } from '~/components/iad/btn/types';

const props = withDefaults(
  defineProps<{
    hashedPhone: string
    size?: BtnSize
    icon?: boolean
  }>(),
  {
    size: 'large',
  },
);

const emit = defineEmits<{
  click: []
}>();

const isDisplayingText = ref(true);
const isMounted = useMounted();

const phone = computed(() => {
  if (!isMounted.value) return ``;
  if (!props.hashedPhone) return ``;
  return atob(props.hashedPhone);
});

const runtimeConfig = useRuntimeConfig();
const DEFAULT_COUNTRY_BY_GEOGRAPHY: Record<string, CountryCode> = {
  france: `FR`,
  espana: `ES`,
  italy: `IT`,
  portugal: `PT`,
} as const;
const defaultCountry = computed(() => {
  if (runtimeConfig.public.geography in DEFAULT_COUNTRY_BY_GEOGRAPHY) {
    return DEFAULT_COUNTRY_BY_GEOGRAPHY[runtimeConfig.public.geography];
  }
  return undefined;
});

const isPhoneNumber = computed(() => {
  if (!phone.value) return false;
  return isValidPhoneNumber(phone.value, {
    defaultCountry: defaultCountry.value,
  });
});
const formattedPhoneNumber = computed(() => {
  if (!phone.value) return ``;
  if (!isPhoneNumber.value) return ``;
  const phoneNumber = parsePhoneNumberWithError(phone.value, {
    defaultCountry: defaultCountry.value,
  });
  return phoneNumber.formatInternational();
});

function revealPhone() {
  if (!isPhoneNumber.value) return;
  isDisplayingText.value = false;
  emit(`click`);
}

const phoneLink = computed(() => {
  if (!isMounted.value) return ``;
  return `tel:${atob(props.hashedPhone)}`;
});

// NOTE: span with grid is used so the button already takes the maximum width
</script>

<template>
  <div class="content">
    <IadBtn
      v-if="icon"
      :size
      :href="phoneLink"
      icon
      @click="emit(`click`)"
    >
      <Icon name="iad-icon:phone" />
    </IadBtn>
    <div v-else>
      <IadBtn
        :size
        class="hidden lg:flex w-full"
        @click="revealPhone"
      >
        <span class="grid">
          <span
            class="row-start-1 row-end-1 col-start-1 col-end-1"
            :class="{ 'invisible pointer-events-none': isDisplayingText }"
            v-text="formattedPhoneNumber"
          />
          <span
            class="row-start-1 row-end-1 col-start-1 col-end-1"
            :class="{ 'invisible pointer-events-none': !isDisplayingText }"
            v-text="$t(`core.common.see_phone`)"
          />
        </span>
      </IadBtn>
      <IadBtn
        :size
        class="lg:hidden flex w-full"
        :href="phoneLink"
        @click="emit(`click`)"
      >
        <span v-text="$t('agent.navigation.call')" />
      </IadBtn>
    </div>
  </div>
</template>
